import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useData } from '../../contexts/DataContext';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Alert,
  styled,
  Tooltip,
  
  FormControl,
  Select,
  MenuItem,
  TextField,
  InputLabel,

  Paper,
  Divider
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { 
  Delete as DeleteIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon,
  Repeat as RepeatIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  SwapHoriz as SwapHorizIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  Add as AddIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import PageHeader from '../common/PageHeader';
import SegmentedControl from '../common/SegmentedControl';
import ToolbarButton from '../common/ToolbarButton';
import ActionFormDialog from './ActionFormDialog';
import ActionsTable from './ActionsTable';
import ActionMatrixView from './ActionMatrixView';
import ActionsCardList from './ActionsCardList';
import ListSection from '../common/ListSection';
import ConfirmDialog from '../common/ConfirmDialog';
import SortIcon from '@mui/icons-material/Sort';
import Menu from '@mui/material/Menu';

import ScrollableFilters from '../Rules/components/ScrollableFilters';
import { useScrollableFilters } from '../Rules/hooks/useScrollableFilters';

// Shared styling for the inline filter fields (white bg so they sit cleanly on the card),
// matching the Transactions filter bar.
const filterFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    backgroundColor: '#fff',
    '&:hover': { backgroundColor: '#fff' },
    '&.Mui-focused': { backgroundColor: '#fff' }
  }
};

// Styled components

const ActionTypeIcon = ({ type, ...props }) => {
  switch (type) {
    case 'income':
      return <TrendingUpIcon {...props} color="success" />;
    case 'expense':
      return <TrendingDownIcon {...props} color="error" />;
    case 'transfer':
      return <SwapHorizIcon {...props} color="info" />;
    default:
      return <ScheduleIcon {...props} />;
  }
};

const FrequencyChip = ({ frequency }) => {
  const getLabel = () => {
    switch (frequency) {
      case 'once':
        return 'One-time';
      case 'daily':
        return 'Daily';
      case 'weekly':
        return 'Weekly';
      case 'monthly':
        return 'Monthly';
      case 'yearly':
        return 'Yearly';
      default:
        return frequency;
    }
  };

  return (
    <Chip 
      size="small" 
      icon={frequency === 'once' ? null : <RepeatIcon />}
      label={getLabel()}
      sx={{ 
        bgcolor: frequency === 'once' ? 'grey.100' : 'primary.light',
        color: frequency === 'once' ? 'text.primary' : 'primary.dark'
      }}
    />
  );
};


const ActionManager = () => {
  const { actions, accounts, categories, addAction, updateAction, deleteAction } = useData();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [actionToDelete, setActionToDelete] = useState(null);
  const [editAction, setEditAction] = useState(null);
  const [formError, setFormError] = useState('');
  const [layoutMode, setLayoutMode] = useState('list'); // list | matrix
  const [matrixYear, setMatrixYear] = useState(new Date().getFullYear());
  const [searchTerm, setSearchTerm] = useState('');
  // Removed type filter row (All/Active/Completed/Income/Expenses/Transfers) per user request
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null); // mobile-only sort menu
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [accountFilter, setAccountFilter] = useState('');
  const [selectedActionIds, setSelectedActionIds] = useState([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: '',
    amount: '',
    type: 'expense',
    sourceAccountId: '',
    destinationAccountId: '',
    startDate: new Date(),
    endDate: null,
    frequency: 'once',
    dayOfMonth: 1,
    dayOfWeek: 1,
    isActive: true,
    isCompleted: false,
    notes: ''
  });
  
  // Reset form to default values
  const resetForm = () => {
    setFormData({
      id: '',
      name: '',
      description: '',
      amount: '',
      type: 'expense',
      sourceAccountId: '',
      destinationAccountId: '',
      startDate: new Date(),
      endDate: null,
      frequency: 'once',
      dayOfMonth: 1,
      dayOfWeek: 1,
      isActive: true,
      isCompleted: false,
      notes: ''
    });
    setFormError('');
  };
  
  // Open dialog for creating or editing an action
  const handleOpenDialog = (action = null) => {
    setEditAction(action);
    setDialogOpen(true);
    setFormError('');
  };
  
  // Handle closing the dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditAction(null);
    setFormError('');
  };
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  // Handle date changes from date pickers
  const handleDateChange = (name, date) => {
    setFormData(prevData => ({
      ...prevData,
      [name]: date
    }));
  };
  
  // Handle form submission
  const handleSubmit = async (formData) => {
    setLoading(true);
    setFormError('');
    try {
      if (editAction) {
        await updateAction(editAction.id, { ...editAction, ...formData });
      } else {
        await addAction(formData);
      }
      handleCloseDialog();
    } catch (err) {
      setFormError('Error saving action: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle action deletion
  const handleDeleteClick = (action) => {
    setActionToDelete(action);
    setDeleteDialogOpen(true);
  };
  
  const confirmDelete = async () => {
    if (!actionToDelete) return;
    setLoading(true);
    try {
      await deleteAction(actionToDelete);
      setDeleteDialogOpen(false);
      setActionToDelete(null);
    } catch (err) {
      setError('Error deleting action: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Bulk delete selected actions
  const handleBulkDelete = async () => {
    if (!selectedActionIds.length) return;
    setLoading(true);
    try {
      for (const id of selectedActionIds) {
        if (!id) continue;
        await deleteAction(id);
      }
      setSelectedActionIds([]);
    } catch (err) {
      setError('Error deleting selected actions: ' + err.message);
    } finally {
      setLoading(false);
      setBulkDeleteDialogOpen(false);
    }
  };

  const handleToggleSelect = (id) => {
    setSelectedActionIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // Legacy bulk complete removed (replaced by delete)
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };
  
  // Format date
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString();
  };
  
  // Get account name by ID
  const getAccountName = (accountId) => {
    const account = accounts.find(acc => acc.id === accountId);
    return account ? account.name : 'Unknown Account';
  };
  
  // Filtered actions (completion deletes, so no need to separate completed)
  const filteredActions = useMemo(() => {
    let list = actions.filter(a => {
      const termOk = !searchTerm || (a.name && a.name.toLowerCase().includes(searchTerm.toLowerCase())) || (a.description && a.description.toLowerCase().includes(searchTerm.toLowerCase()));
      const accountOk = !accountFilter || a.sourceAccountId === accountFilter || a.destinationAccountId === accountFilter;
      return termOk && accountOk;
    });
    list.sort((a,b)=>{
      let av, bv;
      switch (sortBy) {
        case 'amount': av = a.amount; bv = b.amount; break;
        case 'startDate': av = new Date(a.startDate).getTime(); bv = new Date(b.startDate).getTime(); break;
        case 'name': default: av = (a.name||'').toLowerCase(); bv = (b.name||'').toLowerCase();
      }
      if (av < bv) return sortDirection === 'asc' ? -1 : 1;
      if (av > bv) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [actions, searchTerm, accountFilter, sortBy, sortDirection]);

  const activeActions = filteredActions; // All remaining actions

  // Column-header sorting (desktop table)
  const handleSort = (column) => {
    const isAsc = sortBy === column && sortDirection === 'asc';
    setSortBy(column);
    setSortDirection(isAsc ? 'desc' : 'asc');
    setPage(0);
  };

  const accountFiltersScroll = useScrollableFilters();

  const viewOptions = [
    { value: 'list', label: 'List' },
    { value: 'matrix', label: 'Matrix' }
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader
        title="Actions"
        icon={<AssignmentTurnedInIcon fontSize="large" />}
        actions={
          <SegmentedControl
            options={viewOptions}
            value={layoutMode}
            onChange={setLayoutMode}
          />
        }
      />
      {/* Content area padding mirrors TabPanel py spacing used in Budgets */}
  <Box sx={{ py: 0 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {layoutMode === 'list' && (
          <ListSection
            actions={[
              <ToolbarButton key="add" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                Add
              </ToolbarButton>
            ]}
            filters={
              <ScrollableFilters
                scrollRef={accountFiltersScroll.scrollRef}
                showLeftScroll={accountFiltersScroll.showLeftScroll}
                showRightScroll={accountFiltersScroll.showRightScroll}
                onScrollLeft={accountFiltersScroll.scrollLeft}
                onScrollRight={accountFiltersScroll.scrollRight}
              >
                <TextField
                  label="Search"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                  size="small"
                  sx={{ minWidth: 200, ...filterFieldSx }}
                  InputProps={{
                    startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                    style: { fontSize: '0.875rem' }
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 220, ...filterFieldSx }}>
                  <InputLabel id="action-filter-account-label" shrink>Account</InputLabel>
                  <Select
                    labelId="action-filter-account-label"
                    value={accountFilter}
                    label="Account"
                    onChange={(e) => { setAccountFilter(e.target.value); setPage(0); }}
                    displayEmpty
                    sx={{ fontSize: '0.875rem' }}
                  >
                    <MenuItem value="" sx={{ fontSize: '0.875rem' }}>All Accounts</MenuItem>
                    {accounts.map(acc => (
                      <MenuItem key={acc.id} value={acc.id} sx={{ fontSize: '0.875rem' }}>{acc.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </ScrollableFilters>
            }
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, mt: 0, minHeight: 28 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
                Showing {activeActions.length} action{activeActions.length !== 1 ? 's' : ''}
              </Typography>
              {/* Delete only appears once rows are ticked, matching Budgets */}
              {selectedActionIds.length > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DeleteIcon />}
                  onClick={() => setBulkDeleteDialogOpen(true)}
                  disabled={loading}
                  color="error"
                  sx={{
                    ml: 1, borderRadius: 3, fontWeight: 500, fontSize: '0.8125rem',
                    px: 1.5, py: 0.25, minHeight: 0
                  }}
                >
                  Delete ({selectedActionIds.length})
                </Button>
              )}
              {/* On mobile the table headers are gone, so sorting lives in a menu */}
              <Box sx={{ ml: 'auto', display: { xs: 'block', sm: 'none' } }}>
                <Button size="small" startIcon={<SortIcon />} onClick={(e) => setSortMenuAnchor(e.currentTarget)} sx={{ textTransform: 'none', fontSize: '0.8125rem', fontWeight: 500 }}>
                  Sort by
                </Button>
                <Menu anchorEl={sortMenuAnchor} open={Boolean(sortMenuAnchor)} onClose={() => setSortMenuAnchor(null)} keepMounted>
                  {[['name', 'Name'], ['amount', 'Amount'], ['startDate', 'Start Date']].flatMap(([id, label]) => (
                    ['asc', 'desc'].map(dir => (
                      <MenuItem
                        key={`${id}-${dir}`}
                        sx={{ fontSize: '0.8125rem' }}
                        onClick={() => { setSortBy(id); setSortDirection(dir); setPage(0); setSortMenuAnchor(null); }}
                      >
                        {label} {dir === 'asc' ? '↑' : '↓'}
                      </MenuItem>
                    ))
                  ))}
                </Menu>
              </Box>
            </Box>
            <ActionsCardList
              actions={activeActions}
              accounts={accounts}
              onEdit={handleOpenDialog}
              onDelete={handleDeleteClick}
              selectedIds={selectedActionIds}
              onToggleSelect={handleToggleSelect}
              onSelectAll={(checked) => {
                setSelectedActionIds(checked ? activeActions.map(a => a.id || a._id) : []);
              }}
              sortBy={sortBy}
              sortDirection={sortDirection}
              onSort={handleSort}
              page={page}
              rowsPerPage={rowsPerPage}
              setPage={setPage}
              setRowsPerPage={setRowsPerPage}
            />
          </ListSection>
        )}
        {layoutMode === 'matrix' && (
          <ActionMatrixView
            actions={actions}
            accounts={accounts}
            year={matrixYear}
            onYearChange={setMatrixYear}
            onEditAction={handleOpenDialog}
            onDeleteAction={handleDeleteClick}
          />
        )}

        <ActionFormDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          editAction={editAction}
          accounts={accounts}
          categories={categories}
          onSubmit={handleSubmit}
          loading={loading}
          formError={formError}
        />

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          title="Delete Action"
          message={`Are you sure you want to delete the action "${actionToDelete?.name}"? This action cannot be undone.`}
          confirmLabel="Delete"
          onConfirm={confirmDelete}
          loading={loading}
        />

        {/* Bulk Delete Confirmation Dialog */}
        <ConfirmDialog
          open={bulkDeleteDialogOpen}
          onClose={() => setBulkDeleteDialogOpen(false)}
          title={`Delete ${selectedActionIds.length} action${selectedActionIds.length !== 1 ? 's' : ''}?`}
          message="This action cannot be undone."
          confirmLabel="Delete"
          onConfirm={handleBulkDelete}
          loading={loading}
        />
      </Box>
    </Box>
  );
};

export default ActionManager;